import ApiCallerClient, {ApiMethod} from "../clients/apiCallerClient";
import SessionStorageHandler from "./sessionStorageHandler";

export default class VisualQaHandler {

    constructor(apiHost) {
        this.apiHost = apiHost;
        this.sessionStorageHandler = new SessionStorageHandler();
    }

    getVisualQaExperimentalResponses = async (query_text, image_url, is_mmir_grounding_selected, is_ngs_grounding_selected, prompt_mmlm) => {
        try {
            const path = `/demo/compare`;

            // Fetch image bytes and convert to base64 for the comparator
            let imageBase64 = '';
            if (image_url) {
                if (image_url.startsWith('data:image/')) {
                    imageBase64 = image_url;
                } else {
                    try {
                        const imgResponse = await fetch(image_url);
                        const blob = await imgResponse.blob();
                        imageBase64 = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                    } catch (imgErr) {
                        console.error('Failed to fetch image bytes, sending URL instead:', imgErr);
                        imageBase64 = image_url;
                    }
                }
            }

            const leftMode = is_mmir_grounding_selected ? 'vtag_v0' : 'parametric';

            let response = await ApiCallerClient.callApi({
                    "endpoint": {
                        "hostName": this.apiHost,
                        "httpMethod": ApiMethod.POST,
                        "path": path
                    },
                    "body": JSON.stringify({
                        "query": query_text,
                        "image": imageBase64,
                        "left_mode": leftMode
                    })
                });

            const result = response?.body ? JSON.parse(response.body) : response;
            return result;
        } catch (error) {
            console.error('Error calling comparator:', error);
            throw new Error('Failed to call comparator: ' + error);
        }
    }

    constructAnnotationModel = async (query_text, image_url, existing_invoke_ngs, existing_invoke_mmir, prompt_mmlm, existing_response, grounding_images) => {
        try {
            const calls = this.determineApiCalls(existing_invoke_ngs, existing_invoke_mmir);
            const responses = await Promise.all(
                calls.map(call => this.getVisualQaExperimentalResponses(
                    query_text, image_url, call.invoke_mmir, call.invoke_ngs, prompt_mmlm
                ))
            );

            let images = [];
            
            if (existing_invoke_mmir && grounding_images && grounding_images.length > 0) {
                const limitedImages = grounding_images.slice(0, 10);
                images = limitedImages.map(img => ({
                    displayUrl: img.url,
                    sourceUrl: img.source_url || 'N/A',
                    caption: img.source_metadata?.caption || 'N/A',
                    title: img.source_metadata?.title || 'N/A',
                    sourceTitle: img.ims_source_title || 'N/A',
                    sourceSummary: img.ims_source_summary || 'N/A',
                    sourceContent: img.ims_source_content || 'N/A'
                }));
            } else {
                responses.forEach((response, index) => {
                    const call = calls[index];
                    if (call.invoke_mmir && response.mmir_images_grounding) {
                        const limitedImages = response.mmir_images_grounding.slice(0, 10);
                        images = limitedImages.map(img => ({
                            displayUrl: img.url,
                            sourceUrl: img.source_url || 'N/A',
                            caption: img.source_metadata?.caption || 'N/A',
                            title: img.source_metadata?.title || 'N/A',
                            sourceTitle: img.ims_source_title || 'N/A',
                            sourceSummary: img.ims_source_summary || 'N/A',
                            sourceContent: img.ims_source_content || 'N/A'
                        }));
                        console.error("Images: " + JSON.stringify(images))
                    }
                });
            }

            const answers = {};
            
            if (existing_invoke_ngs && !existing_invoke_mmir) {
                answers.text_grounding_answer = existing_response.mmlm_answer || existing_response;
            } else if (!existing_invoke_ngs && existing_invoke_mmir) {
                answers.image_grounding_answer = existing_response.mmlm_answer || existing_response;
            } else if (!existing_invoke_ngs && !existing_invoke_mmir) {
                answers.parametric_answer = existing_response.mmlm_answer || existing_response;
            }
            
            responses.forEach((response, index) => {
                const call = calls[index];
                if (call.invoke_ngs && !call.invoke_mmir && !answers.text_grounding_answer) {
                    answers.text_grounding_answer = response.mmlm_answer || response;
                } else if (!call.invoke_ngs && call.invoke_mmir && !answers.image_grounding_answer) {
                    answers.image_grounding_answer = response.mmlm_answer || response;
                } else if (!call.invoke_ngs && !call.invoke_mmir && !answers.parametric_answer) {
                    answers.parametric_answer = response.mmlm_answer || response;
                }
            });

            return {
                images,
                ...answers
            };
        } catch (error) {
            console.error('Error constructing annotation model:', error);
            throw new Error('Failed to construct annotation model: ' + error);
        }
    }

    determineApiCalls = (existing_invoke_ngs, existing_invoke_mmir) => {
        if (existing_invoke_ngs && existing_invoke_mmir) {
            return [
                { invoke_ngs: true, invoke_mmir: false },
                { invoke_ngs: false, invoke_mmir: false },
                { invoke_ngs: false, invoke_mmir: true }
            ];
        } else if (existing_invoke_ngs && !existing_invoke_mmir) {
            return [
                { invoke_ngs: false, invoke_mmir: true },
                { invoke_ngs: false, invoke_mmir: false }
            ];
        } else if (!existing_invoke_ngs && existing_invoke_mmir) {
            return [
                { invoke_ngs: true, invoke_mmir: false },
                { invoke_ngs: false, invoke_mmir: false }
            ];
        } else {
            return [
                { invoke_ngs: true, invoke_mmir: false },
                { invoke_ngs: false, invoke_mmir: true }
            ];
        }
    }
}
